import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { useWorkspacePlan } from '../../../hooks/useWorkspacePlan';
import { VideoEditorLinkIcon } from '../../VideoEditorPage/icons/videoEditorLinkIcon';
import ReactPlayer from 'react-player';
import { DownloadIcon } from '../../../components/icons/DownloadIcon';
import { useEffect, useState } from 'react';
import { downloadGCSFile } from './PublishMenus';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { InfoIcon } from '../../VideoEditorPage/icons/infoIcon';
import classNames from '../../../helper/classNames';

const DownloadButton = ({
  setIsDownloading,
  setDownloadError,
  downloadUrl,
  title,
  aspectRatio,
  isDownloading,
}) => (
  <button
    className="flex absolute w-6 h-6 bg-white bottom-2 right-2 rounded-full justify-center items-center"
    onClick={(e) => {
      e.stopPropagation();
      if (!downloadUrl) return;
      downloadGCSFile(
        downloadUrl,
        `${title}-${aspectRatio}.mp4`,
        setIsDownloading,
        setDownloadError
      );
    }}
    disabled={isDownloading}
  >
    {isDownloading ? (
      <LoadingSpinner width={'10px'} />
    ) : (
      <DownloadIcon className="fill-black" width="10px" height="10px" />
    )}
  </button>
);

export const VideoPreviewModal = ({
  closeModal,
  selectedPlaybackUrl,
  selectedDownloadUrl,
  title,
  selectedAspectRatio,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="flex w-full h-full fixed inset-0 z-[1001] bg-[#101111F2] max-h-[100vh]">
      <div className="flex w-full h-full justify-center items-center">
        <div className="flex flex-col gap-2 h-[80%] max-h-[80%] w-fit">
          <div className="flex w-full justify-end">
            <button
              className="text-red text-2xl w-8 h-8 rounded-full justify-center items-center flex bg-white"
              onClick={() => closeModal()}
            >
              ×
            </button>
          </div>
          <div className="flex h-[90%]">
            <ReactPlayer
              className={classNames(
                selectedAspectRatio === 'portrait' && 'aspect-[9/16]'
              )}
              width="auto"
              height="100%"
              url={selectedPlaybackUrl}
              controls={true}
              loop={false}
              volume={1}
              muted={false}
              pip={false}
              playing={true}
            />
          </div>

          {selectedDownloadUrl && (
            <div className="flex w-full justify-end">
              <button
                className="flex w-8 h-8 bg-white rounded-full justify-center items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!selectedDownloadUrl) return;
                  downloadGCSFile(
                    selectedDownloadUrl,
                    `${title}-${selectedAspectRatio}.mp4`,
                    setIsDownloading
                  );
                }}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <LoadingSpinner width={'14px'} />
                ) : (
                  <DownloadIcon
                    className="fill-black"
                    width="14px"
                    height="14px"
                  />
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const VideoPreviews = ({
  highlights,
  setShowUpgradeModal,
  workspace,
  series,
  shared = false,
}) => {
  const { title, meetingSeriesId } = series || {};
  const { workspacePlan } = useWorkspacePlan();
  const { workspaceId } = workspace || {};

  const [isDownloadingSquare, setIsDownloadingSquare] = useState(false);
  const [isDownloadingPortrait, setIsDownloadingPortrait] = useState(false);
  const [isDownloadingLandscape, setIsDownloadingLandscape] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const [selectedPlaybackUrl, setSelectedPlaybackUrl] = useState(null);
  const [selectedDownloadUrl, setSelectedDownloadUrl] = useState(null);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const firstHighlight = highlights[0];
  const {
    highlightId,
    HIGHLIGHT_EDITED_PORTRAIT,
    HIGHLIGHT_EDITED_LANDSCAPE,
    HIGHLIGHT_EDITED_SQUARE,
    HIGHLIGHT_SQUARE_THUMBNAIL,
    HIGHLIGHT_PORTRAIT_THUMBNAIL,
    HIGHLIGHT_THUMBNAIL,
    segments = [],
  } = firstHighlight || {};

  const wasRecordedInMobile = (segments || []).some(
    (s) => s?.device === 'mobile'
  );
  const showUpgradeText =
    !HIGHLIGHT_EDITED_LANDSCAPE?.playbackUrl &&
    !HIGHLIGHT_EDITED_PORTRAIT?.playbackUrl &&
    workspacePlan === 'free';

  const openModal = (downloadUrl, playbackUrl, aspectRatio) => {
    setSelectedPlaybackUrl(playbackUrl);
    setSelectedDownloadUrl(downloadUrl);
    setSelectedAspectRatio(aspectRatio);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedPlaybackUrl(null);
    setSelectedDownloadUrl(null);
    setSelectedAspectRatio(null);
    setShowModal(null);
  };

  if (!firstHighlight) return null;

  return (
    <>
      {showModal &&
        selectedPlaybackUrl &&
        selectedDownloadUrl &&
        selectedAspectRatio && (
          <VideoPreviewModal
            closeModal={closeModal}
            selectedDownloadUrl={selectedDownloadUrl}
            selectedPlaybackUrl={selectedPlaybackUrl}
            selectedAspectRatio={selectedAspectRatio}
            title={title}
          />
        )}
      <div
        className={classNames(
          'flex flex-col gap-3 w-full bg-[#FAFAFA] shadow-[0px_5px_7.3px_rgba(0,0,0,0.15),_0px_1px_1px_rgba(0,0,0,0.05)] border border-[#D9D9D9] rounded-[15px] pt-7 pb-[42px] px-9'
        )}
      >
        <div className="flex justify-between w-full items-center">
          <span className="text-[#111111] font-roboto text-lg font-bold">
            Your Ready-to-Publish Videos
          </span>
          <div
            onClick={
              workspacePlan === 'free'
                ? () => setShowUpgradeModal(true)
                : undefined
            }
          >
            <Link
              to={
                workspacePlan === 'free'
                  ? undefined
                  : shared
                  ? `/editor/${meetingSeriesId}`
                  : `/clip/${highlightId}/edit?workspaceId=${workspaceId}`
              }
              className="flex bg-purple w-fit text-sm font-medium items-center gap-2.5 group relative cursor-pointer py-2 px-4 rounded-md hover:scale-105 transition-transform"
            >
              <>
                <VideoEditorLinkIcon width={20} height={20} color="white" />
                <span className="text-base uppercase text-white font-medium">
                  Video Editor
                </span>
              </>
            </Link>
          </div>
        </div>
        <div className="flex gap-3 justify-center items-center max-h-[214px] h-[214px]">
          {HIGHLIGHT_EDITED_SQUARE?.playbackUrl && (
            <div
              className="flex w-fit h-full relative hover:cursor-pointer"
              onClick={() =>
                openModal(
                  HIGHLIGHT_EDITED_SQUARE?.downloadUrl,
                  HIGHLIGHT_EDITED_SQUARE?.playbackUrl,
                  'square'
                )
              }
            >
              {HIGHLIGHT_SQUARE_THUMBNAIL?.imageUrl && (
                <img
                  src={HIGHLIGHT_SQUARE_THUMBNAIL.imageUrl}
                  className="object-contain absolute top-0 left-0 h-full w-auto"
                  alt=""
                />
              )}
              <ReactPlayer
                width="auto"
                height="100%"
                url={HIGHLIGHT_EDITED_SQUARE?.playbackUrl}
                controls={false}
                loop={false}
                volume={1}
                muted={false}
                pip={false}
                playing={false}
              />
              <DownloadButton
                setIsDownloading={setIsDownloadingSquare}
                setDownloadError={setDownloadError}
                downloadUrl={HIGHLIGHT_EDITED_SQUARE?.downloadUrl}
                title={title}
                aspectRatio={'square'}
                isDownloading={isDownloadingSquare}
              />
            </div>
          )}
          {HIGHLIGHT_EDITED_PORTRAIT?.playbackUrl && (
            <div
              className="flex w-fit h-full relative hover:cursor-pointer"
              onClick={() =>
                openModal(
                  HIGHLIGHT_EDITED_PORTRAIT?.downloadUrl,
                  HIGHLIGHT_EDITED_PORTRAIT?.playbackUrl,
                  'portrait'
                )
              }
            >
              {HIGHLIGHT_PORTRAIT_THUMBNAIL?.imageUrl && (
                <img
                  src={HIGHLIGHT_PORTRAIT_THUMBNAIL.imageUrl}
                  className="object-contain absolute top-0 left-0 h-full w-auto"
                  alt=""
                />
              )}
              <ReactPlayer
                width="auto"
                height="100%"
                url={HIGHLIGHT_EDITED_PORTRAIT?.playbackUrl}
                controls={false}
                loop={false}
                volume={1}
                muted={false}
                pip={false}
                playing={false}
              />
              <DownloadButton
                setIsDownloading={setIsDownloadingPortrait}
                setDownloadError={setDownloadError}
                downloadUrl={HIGHLIGHT_EDITED_PORTRAIT?.downloadUrl}
                title={title}
                aspectRatio={'portrait'}
                isDownloading={isDownloadingPortrait}
              />
            </div>
          )}
          {HIGHLIGHT_EDITED_LANDSCAPE?.playbackUrl && (
            <div
              className="flex w-fit h-full relative hover:cursor-pointer"
              onClick={() =>
                openModal(
                  HIGHLIGHT_EDITED_LANDSCAPE?.downloadUrl,
                  HIGHLIGHT_EDITED_LANDSCAPE?.playbackUrl,
                  'landscape'
                )
              }
            >
              {HIGHLIGHT_THUMBNAIL?.imageUrl && (
                <img
                  src={HIGHLIGHT_THUMBNAIL.imageUrl}
                  className="object-contain absolute top-0 left-0 h-full w-auto"
                  alt=""
                />
              )}
              <ReactPlayer
                width="auto"
                height="100%"
                url={HIGHLIGHT_EDITED_LANDSCAPE?.playbackUrl}
                controls={false}
                loop={false}
                volume={1}
                muted={false}
                pip={false}
                playing={false}
              />
              <DownloadButton
                setIsDownloading={setIsDownloadingLandscape}
                setDownloadError={setDownloadError}
                downloadUrl={HIGHLIGHT_EDITED_LANDSCAPE?.downloadUrl}
                title={title}
                aspectRatio={'landscape'}
                isDownloading={isDownloadingLandscape}
              />
            </div>
          )}
        </div>
        {downloadError && (
          <div className="flex w-full justify-center text-red font-medium text-sm">
            {downloadError}
          </div>
        )}
        {wasRecordedInMobile && (
          <div className="flex justify-center items-center w-full gap-3">
            <InfoIcon fill="#F9155B" width="23" height="23" />
            <span className="text-black text-xs">
              No landscape 16:9 format —session was recorded on mobile app.
            </span>
          </div>
        )}
        {showUpgradeText && (
          <div className="flex justify-center items-center w-full gap-3">
            <Link
              className="bg-purple rounded-[5px] text-white font-roboto text-xs active:translate-y-0.5 w-fit h-fit px-2 py-1"
              to={`/workspace/billing?workspaceId=${workspaceId}`}
            >
              Upgrade your plan
            </Link>
            <span className="text-black text-xs">
              for Landscape and Portrait version
            </span>
          </div>
        )}
      </div>
    </>
  );
};
